import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./context/AuthContext";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import Analyzer from "./pages/Analyzer";
import Generator from "./pages/Generator";
import Portfolio from "./pages/Portfolio";
import Tracker from "./pages/Tracker";
import Learn from "./pages/Learn";

const TABS = [
  { id: "dashboard", label: "لوحة التحكم", icon: "◈" },
  { id: "analyzer", label: "محلل الدومينات", icon: "◉" },
  { id: "generator", label: "مولّد الأفكار", icon: "◇" },
  { id: "portfolio", label: "محفظتي", icon: "▣" },
  { id: "tracker", label: "المبيعات", icon: "◆" },
  { id: "learn", label: "تعلّم", icon: "◎" },
];

export default function App() {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [domains, setDomains] = useState([]);
  const [sales, setSales] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    setDataLoading(true);
    try {
      const [d, s] = await Promise.all([axios.get("/api/domains"), axios.get("/api/sales")]);
      setDomains(d.data);
      setSales(s.data);
    } catch (err) {
      console.error("خطأ في تحميل البيانات", err);
    }
    setDataLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0f0d0a", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#c8a96e", fontSize: 40 }}>◈</div>
    </div>
  );

  if (!user) return <AuthPage />;

  const pages = {
    dashboard: <Dashboard domains={domains} sales={sales} />,
    analyzer: <Analyzer />,
    generator: <Generator />,
    portfolio: <Portfolio domains={domains} onRefresh={loadData} />,
    tracker: <Tracker sales={sales} onRefresh={loadData} />,
    learn: <Learn />,
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0f0d0a", color: "#fff", fontFamily: "'Segoe UI', Tahoma, sans-serif", direction: "rtl" }}>

      {/* Header */}
      <div style={{ background: "rgba(200,169,110,0.05)", borderBottom: "1px solid rgba(200,169,110,0.12)", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 200, backdropFilter: "blur(12px)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#c8a96e", fontSize: 22 }}>◈</span>
          <div>
            <div style={{ color: "#c8a96e", fontWeight: 800, fontSize: 16, letterSpacing: 1 }}>دومين برو</div>
            <div style={{ color: "#555", fontSize: 10 }}>مرحباً، {user.name}</div>
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <button onClick={() => setMenuOpen(!menuOpen)}
            style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 12px", color: "#aaa", cursor: "pointer", fontSize: 13 }}>
            ☰
          </button>
          {menuOpen && (
            <div style={{ position: "absolute", left: 0, top: "calc(100% + 8px)", background: "#1a1612", border: "1px solid rgba(200,169,110,0.2)", borderRadius: 10, padding: 8, minWidth: 160, zIndex: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
              <div style={{ color: "#666", fontSize: 11, padding: "4px 10px", marginBottom: 4 }}>{user.email}</div>
              <button onClick={() => { logout(); setMenuOpen(false); }}
                style={{ width: "100%", background: "rgba(196,124,124,0.1)", border: "1px solid rgba(196,124,124,0.2)", borderRadius: 7, padding: "9px 14px", color: "#c47c7c", cursor: "pointer", fontSize: 13, textAlign: "right", fontFamily: "inherit" }}>
                تسجيل الخروج
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab Title */}
      <div style={{ padding: "20px 20px 0" }}>
        <div style={{ color: "#c8a96e", fontSize: 11, letterSpacing: 3, opacity: 0.6 }}>
          {TABS.find((t) => t.id === activeTab)?.label}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "16px 20px 100px", maxWidth: 900, margin: "0 auto" }}>
        {dataLoading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#555" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>◈</div>
            <div>جارٍ تحميل البيانات...</div>
          </div>
        ) : pages[activeTab]}
      </div>

      {/* Bottom Nav */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(12,10,7,0.97)", borderTop: "1px solid rgba(200,169,110,0.1)", padding: "8px 0 16px", display: "flex", justifyContent: "space-around", backdropFilter: "blur(20px)", zIndex: 100 }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ flex: 1, background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "6px 0" }}>
            <span style={{ fontSize: 16, color: activeTab === t.id ? "#c8a96e" : "#3a3530", transition: "all .2s" }}>{t.icon}</span>
            <span style={{ fontSize: 9, color: activeTab === t.id ? "#c8a96e" : "#3a3530", letterSpacing: 0.5 }}>{t.label.substring(0, 4)}</span>
            {activeTab === t.id && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#c8a96e" }} />}
          </button>
        ))}
      </div>
    </div>
  );
}
